import React, { useState, useMemo } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { getStatusColor } from '../../data/helpers';

const { FiSearch, FiFilter, FiAlertTriangle, FiCheckCircle, FiClock, FiChevronUp, FiChevronDown } = FiIcons;

const reqTypeColor = {
  'Security Awareness': 'bg-blue-100 text-blue-700',
  'Policy Acknowledgement': 'bg-purple-100 text-purple-700',
  'Compliance Training': 'bg-teal-100 text-teal-700',
  'Onboarding': 'bg-violet-100 text-violet-700',
  'Remedial': 'bg-orange-100 text-orange-700',
  'Phishing Simulation': 'bg-rose-100 text-rose-700'
};

const getDaysOverdue = (dueDate, completionDate) => {
  if (completionDate || !dueDate) return null;
  const diff = Math.floor((new Date() - new Date(dueDate)) / (1000 * 60 * 60 * 24));
  return diff > 0 ? diff : null;
};

const STATUSES = ['All', 'Compliant', 'Overdue', 'At Risk', 'In Progress', 'Not Started'];
const REQ_TYPES = ['All', 'Security Awareness', 'Policy Acknowledgement', 'Compliance Training', 'Onboarding', 'Remedial', 'Phishing Simulation'];

const ComplianceTable = ({ records = [], learners = [], campaigns = [] }) => {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('All');
  const [reqType, setReqType] = useState('All');
  const [dept, setDept] = useState('All');
  const [sortKey, setSortKey] = useState('dueDate');
  const [sortDir, setSortDir] = useState('asc');

  const depts = ['All', ...new Set(learners.map(l => l?.department).filter(Boolean))];

  const enriched = useMemo(() => records.map(r => {
    const learner = learners.find(l => String(l?.id) === String(r?.learnerId));
    const campaign = campaigns.find(c => String(c?.id) === String(r?.requiredTraining));
    const daysOverdue = getDaysOverdue(r?.dueDate, r?.completionDate);
    return { ...r, learner, campaign, daysOverdue };
  }), [records, learners, campaigns]);

  const filtered = useMemo(() => {
    let rows = enriched.filter(r => {
      const name = r.learner ? `${r.learner.firstName} ${r.learner.lastName}`.toLowerCase() : '';
      const matchSearch = !search || name.includes(search.toLowerCase()) || r.requirement?.toLowerCase().includes(search.toLowerCase());
      const matchStatus = status === 'All' || r.complianceStatus === status;
      const matchType = reqType === 'All' || r.requirementType === reqType;
      const matchDept = dept === 'All' || r.learner?.department === dept;
      return matchSearch && matchStatus && matchType && matchDept;
    });
    rows.sort((a, b) => {
      let aVal = a[sortKey] ?? '';
      let bVal = b[sortKey] ?? '';
      if (sortKey === 'learnerName') { aVal = a.learner ? `${a.learner.firstName} ${a.learner.lastName}` : ''; bVal = b.learner ? `${b.learner.firstName} ${b.learner.lastName}` : ''; }
      return sortDir === 'asc' ? String(aVal).localeCompare(String(bVal)) : String(bVal).localeCompare(String(aVal));
    });
    return rows;
  }, [enriched, search, status, reqType, dept, sortKey, sortDir]);

  const toggleSort = key => { if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortKey(key); setSortDir('asc'); } };
  const Th = ({ label, sKey }) => (
    <th className="p-4 cursor-pointer select-none hover:bg-slate-100 transition-colors" onClick={() => toggleSort(sKey)}>
      <div className="flex items-center gap-1">
        <span>{label}</span>
        {sortKey === sKey ? <SafeIcon icon={sortDir === 'asc' ? FiChevronUp : FiChevronDown} className="text-blue-500 text-xs" /> : <SafeIcon icon={FiChevronDown} className="text-slate-300 text-xs" />}
      </div>
    </th>
  );

  const overdueCount = filtered.filter(r => r.complianceStatus === 'Overdue').length;

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 space-y-3">
        <div className="flex gap-3 flex-col sm:flex-row">
          <div className="relative flex-1">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search learner or training…"
              className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500" />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {STATUSES.map(s => (
            <button key={s} onClick={() => setStatus(s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${status === s ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}>
              {s}
            </button>
          ))}
          <div className="border-l border-slate-200 mx-1" />
          <select value={reqType} onChange={e => setReqType(e.target.value)}
            className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-medium text-slate-600 focus:outline-none focus:border-blue-500">
            {REQ_TYPES.map(t => <option key={t}>{t}</option>)}
          </select>
          <select value={dept} onChange={e => setDept(e.target.value)}
            className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-medium text-slate-600 focus:outline-none focus:border-blue-500">
            {depts.map(d => <option key={d}>{d}</option>)}
          </select>
          {(search || status !== 'All' || reqType !== 'All' || dept !== 'All') && (
            <button onClick={() => { setSearch(''); setStatus('All'); setReqType('All'); setDept('All'); }}
              className="text-xs text-blue-600 hover:underline font-medium px-2">Clear filters</button>
          )}
        </div>
      </div>

      {overdueCount > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
          <SafeIcon icon={FiAlertTriangle} className="text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-red-700">{overdueCount} overdue compliance record{overdueCount !== 1 ? 's' : ''}</p>
            <p className="text-xs text-red-600 mt-0.5">These learners have missed their required training deadlines. Immediate follow-up required.</p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                <Th label="Learner" sKey="learnerName" />
                <th className="p-4">Department</th>
                <Th label="Requirement" sKey="requirementType" />
                <Th label="Training/Test" sKey="requirement" />
                <Th label="Due Date" sKey="dueDate" />
                <Th label="Completed" sKey="completionDate" />
                <th className="p-4">Overdue</th>
                <Th label="Status" sKey="complianceStatus" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm">
              {filtered.map(row => (
                <tr key={row.id} className="hover:bg-slate-50 transition-colors">
                  <td className="p-4">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-violet-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                        {row.learner ? `${row.learner.firstName?.[0] || ''}${row.learner.lastName?.[0] || ''}` : '?'}
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">{row.learner ? `${row.learner.firstName} ${row.learner.lastName}` : row.learnerId}</p>
                        <p className="text-xs text-slate-400">{row.learner?.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4 text-xs text-slate-600">
                    <p>{row.learner?.department}</p>
                    <p className="text-slate-400">{row.learner?.manager}</p>
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${reqTypeColor[row.requirementType] || 'bg-slate-100 text-slate-600'}`}>{row.requirementType}</span>
                  </td>
                  <td className="p-4 text-xs text-slate-700 max-w-36 truncate">{row.requirement || row.requiredTraining}</td>
                  <td className="p-4 text-xs text-slate-600">{row.dueDate || '—'}</td>
                  <td className="p-4">
                    {row.completionDate ? (
                      <div className="flex items-center gap-1.5 text-emerald-600">
                        <SafeIcon icon={FiCheckCircle} className="text-xs" />
                        <span className="text-xs font-medium">{row.completionDate}</span>
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </td>
                  <td className="p-4">
                    {row.daysOverdue ? (
                      <div className="flex items-center gap-1.5">
                        <SafeIcon icon={FiAlertTriangle} className="text-red-500 text-xs" />
                        <span className="text-xs font-bold text-red-600">{row.daysOverdue}d</span>
                      </div>
                    ) : row.completionDate ? (
                      <span className="text-xs text-emerald-600 font-medium">On time</span>
                    ) : (
                      <div className="flex items-center gap-1 text-slate-400">
                        <SafeIcon icon={FiClock} className="text-xs" />
                        <span className="text-xs">Pending</span>
                      </div>
                    )}
                  </td>
                  <td className="p-4">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${getStatusColor(row.complianceStatus || '')}`}>
                      {row.complianceStatus || 'Unknown'}
                    </span>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="p-10 text-center text-slate-400 text-sm">No compliance records match your filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-slate-100 bg-slate-50 text-xs text-slate-500">
          Showing {filtered.length} of {records.length} records
        </div>
      </div>
    </div>
  );
};

export default ComplianceTable;